import React, { useEffect, useState } from "react";
import { Card, Form } from 'react-bootstrap';
import SecondaryButton from '../../../components/common/SecondaryButton';
import PrimaryButton from '../../../components/common/PrimaryButton';
import AutomationService from '../../../services/AutomationService';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library
import { FollowUpTimingEnum } from '../../../enums/FollowUpTimingEnum';

const LeadAutomation = () => {
    const [showSettings, setShowSettings] = useState(true);
    const [isFollowupDateExpanded, setIsFollowupDateExpanded] = useState(false);
    const [isTemplateReplyExpanded, setIsTemplateReplyExpanded] = useState(false);
    const [isTemplateReplyDateExpanded, setIsTemplateReplyDateExpanded] = useState(false);
    const [followUpTemplates, setFollowUpTemplates] = useState([{
        id: Date.now(),
        template: '',
        days: null
    }]);

    const [settings, setSettings] = useState({
        settingId:0,
        isLeadAddChecked: false,
        isLeadStatusUpdateChecked: false,
        isFollowUpDateChecked: false,
        isAutomatedReplyChecked: false,
        leadFollowUpTimings: [],
        leadAutomationReplyEmailMappings: []
    });

    const [emailTemplates, setEmailTemplates] = useState([]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await AutomationService.fetchLeadFollowUpSettings();
                if (response.data?.success && response.data) {
                    const data = response.data.data;
                    setSettings({
                        settingId: data.id,
                        isLeadAddChecked: data.isLeadAddChecked,
                        isLeadStatusUpdateChecked: data.isLeadStatusUpdateChecked,
                        isFollowUpDateChecked: data.isFollowUpDateChecked,
                        isAutomatedReplyChecked: data.isAutomatedReplyChecked,
                        leadFollowUpTimings: data.leadFollowUpTimings,
                        leadAutomationReplyEmailMappings: data.leadAutomationReplyEmailMappings
                    });

                    // Initialize Followup Date section
                    if (data.leadFollowUpTimings.length > 0) {
                        const timing = data.leadFollowUpTimings[0];
                        setIsFollowupDateExpanded(true);
                        // Assuming timeValue corresponds to the radio button value
                        setSettings(prev => ({
                            ...prev,
                            leadFollowUpTimings: [{ ...timing }]
                        }));
                    }

                    // Initialize Automated Template Reply section
                    if (data.leadAutomationReplyEmailMappings.length > 0) {
                        const mappings = data.leadAutomationReplyEmailMappings.map(mapping => ({
                            id: mapping.id,
                            template: mapping.emailFollowUpTemplateId,
                            days: mapping.timeValue != null && Number(mapping.timeValue) > 0 ? Number(mapping.timeValue) : null
                        }));
                        setFollowUpTemplates(mappings);
                    }
                }
            } catch (error) {
                console.error('Error fetching lead follow-up settings:', error);
            }
        };

        const fetchEmailTemplates = async () => {
            try {
                const response = await AutomationService.fetchAllEmailFollowUpTemplates();
                if (response.data?.success && response.data.data) {
                    setEmailTemplates(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching email templates:', error);
            }
        };

        fetchSettings();
        fetchEmailTemplates();
    }, []);

    const handleSave = async () => {
        try {
            const settingId = settings.settingId || uuidv4();
            const payload = {
                id: settingId, // Use ID from fetched settings if it exists
                isLeadAddChecked: settings.isLeadAddChecked,
                isLeadStatusUpdateChecked: settings.isLeadStatusUpdateChecked,
                isFollowUpDateChecked: settings.isFollowUpDateChecked,
                isAutomatedReplyChecked: settings.isAutomatedReplyChecked,
                leadFollowUpTimings: settings.leadFollowUpTimings.map(timing => ({
                    id: timing.id || uuidv4(), // Generate a new Guid if id is not provided
                    leadAutomationSettingId: settingId, // Replace with actual setting ID if needed
                    timeValue: timing.timeValue,
                    timeUnit: FollowUpTimingEnum.Minutes, // Default to 'Days' if not provided
                    offsetDescription: timing.offsetDescription || ""
                })),
                leadAutomationReplyEmailMappings: followUpTemplates.map(template => ({
                    id: template.id || 0, // Use mapping ID from fetched lead follow-up settings if it exists
                    leadAutomationSettingId: settingId, // Replace with actual setting ID if needed
                    emailFollowUpTemplateId: template.template, // Assign selected email template ID
                    timeValue: template.days !== null && template.days !== '' ? Number(template.days) : 0,
                    timeUnit: FollowUpTimingEnum.Days, // Replace with actual time unit if needed
                    offsetDescription: ""
                }))
            };

            const response = await AutomationService.AddUpdateLeadFollowUpSetting(payload);
            if (response.data?.success) {
                alert("Settings saved successfully!");
            } else {
                alert("Failed to save settings. Please try again.");
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            alert("An error occurred while saving settings. Please try again.");
        }
    };

    const handleAddFollowUpTemplate = () => {
        setFollowUpTemplates(prev => [
            ...prev,
            { id: Date.now(), template: '', days: null }
        ]);
    };

    const handleUpdateFollowUpTemplate = (id, field, value) => {
        if (field === 'template') {
            const isDuplicate = followUpTemplates.some(t => t.id !== id && String(t.template) === String(value) && value !== '');
            if (isDuplicate) {
                const selected = emailTemplates.find(e => String(e.id) === String(value));
                const name = selected ? selected.name : 'This template';
                alert(`${name} is already selected in another follow-up. Please choose a different template.`);
                return;
            }
        }

        setFollowUpTemplates(prev => prev.map(template =>
            template.id === id ? { ...template, [field]: value } : template
        ));
    };

    const handleRemoveFollowUpTemplate = (id) => {
        setFollowUpTemplates(prev => prev.filter(template => template.id !== id));
    };

    return (
        <div>
        {/* Action Buttons - Right aligned */}
            <div className="d-flex justify-content-end gap-3 mb-4">               
                <PrimaryButton onClick={handleSave}>
                Save
                </PrimaryButton>
            </div>

            {/* Lead Generation Automation Designer */}
            <div className="text-center mb-5">
                <h3 className="fw-bold mb-2">Automation Triggers</h3>
                <p className="text-muted">Configure automation triggers and manage communication templates</p>
            </div>

            <div className="d-flex align-items-center justify-content-center gap-4 mb-5">
                <Card className="automation-card text-center" style={{ width: '1000px', padding: '20px' }}>
                  <h6 className="text-start fw-bold mb-2">Automation Triggers</h6>
                  <p className="text-start text-muted small mb-3">Select when you want automation to be triggered</p>

                  <div>
                    <Card className="border-0 bg-light">
                        <Card.Body className="p-3">
                            <div className="text-start">
                                <Form.Check 
                                    type="checkbox" 
                                    label="Lead Added" 
                                    checked={settings.isLeadAddChecked} 
                                    onChange={(e) => setSettings(prev => ({ ...prev, isLeadAddChecked: e.target.checked }))}
                                />                                 
                                <p>
                                    Trigger automation when a new lead is added to the system
                                </p>                            
                            </div>
                        </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="border-0 bg-light mt-3">
                        <Card.Body className="p-3">
                            <div className="text-start">
                                <Form.Check 
                                    type="checkbox" 
                                    label="Status Update" 
                                    checked={settings.isLeadStatusUpdateChecked} 
                                    onChange={(e) => setSettings(prev => ({ ...prev, isLeadStatusUpdateChecked: e.target.checked }))}
                                />                                 
                                <p>
                                    Trigger automation whenever a lead's status changes
                                </p>                            
                            </div>
                        </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="border-0 bg-light mt-3">
                        <Card.Body className="p-3">
                            <div 
                             className="text-start"
                             style={{ cursor: 'pointer' }}
                             onClick={() => setIsFollowupDateExpanded(!isFollowupDateExpanded)}
                             >
                                <Form.Check 
                                    type="checkbox" 
                                    label="Followup Date" 
                                    checked={settings.isFollowUpDateChecked} 
                                    onChange={(e) => setSettings(prev => ({ ...prev, isFollowUpDateChecked: e.target.checked }))}
                                />                                 
                                <p>
                                    Trigger automation before a scheduled followup date
                                </p>                            
                            </div>
                            {settings.isFollowUpDateChecked && (
                                    <div className="text-start mt-4">
                                    <div className="row">
                                        <div>
                                        <p>
                                            Send reminder before:
                                        </p> 
                                        <Form.Group className="mb-3">
                                            <div className="mb-2">
                                                <Form.Check
                                                label="15 minutes before"
                                                value="15"
                                                name="loginMethod"
                                                type="radio"                                                                
                                                checked={settings.leadFollowUpTimings[0]?.timeValue === 15}
                                                onChange={() => setSettings(prev => ({
                                                    ...prev,
                                                    leadFollowUpTimings: [{ ...prev.leadFollowUpTimings[0], timeValue: 15 }]
                                                }))}
                                                />
                                            </div>
                                            <div>
                                                <Form.Check
                                                label="5 minutes before"
                                                value="5"
                                                name="loginMethod"
                                                type="radio"                                                                
                                                checked={settings.leadFollowUpTimings[0]?.timeValue === 5}
                                                onChange={() => setSettings(prev => ({
                                                    ...prev,
                                                    leadFollowUpTimings: [{ ...prev.leadFollowUpTimings[0], timeValue: 5 }]
                                                }))}
                                                />
                                            </div>
                                        </Form.Group>
                                        </div>                                          
                                    </div>
                                    </div>
                                )}
                        </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="border-0 bg-light mt-3">
                        <Card.Body className="p-3">
                            <div className="text-start"
                                 style={{ cursor: 'pointer' }}
                                 onClick={() => setIsTemplateReplyExpanded(!isTemplateReplyExpanded)}
                             >
                                <Form.Check 
                                    type="checkbox" 
                                    label="Automated Template Reply" 
                                    checked={settings.isAutomatedReplyChecked} 
                                    onChange={(e) => setSettings(prev => ({ ...prev, isAutomatedReplyChecked: e.target.checked }))}
                                />                                 
                                <p>
                                    Automatically send template responses based on lead interactions
                                </p>                            
                            </div>
                            {settings.isAutomatedReplyChecked && (
                                <div className="text-start mt-4">
                                    <div className="row">
                                        <div className="text-start mt-4">
                                            {followUpTemplates.map((template, index) => (
                                                <div key={template.id} className="row mb-3">
                                                    <div style={{ borderBottom: "1px solid #ccc", margin: "10px 0" }}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>When a lead messages, use template:</Form.Label>
                                                            <Form.Select
                                                                value={template.template}
                                                                onChange={(e) => handleUpdateFollowUpTemplate(template.id, 'template', e.target.value)}
                                                            >
                                                                <option value="">Select Template</option>
                                                                {emailTemplates.map((emailTemplate) => (
                                                                    <option key={emailTemplate.id} value={emailTemplate.id}>
                                                                        {emailTemplate.name}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                    <div className="text-start">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Send follow-up after specific days"
                                                            checked={template.days !== null}
                                                            onChange={(e) => handleUpdateFollowUpTemplate(template.id, 'days', e.target.checked ? '' : null)}
                                                        />
                                                    </div>
                                                    {template.days !== null && (
                                                        <div className="text-start mt-4">
                                                            <div className="row">
                                                                <div>
                                                                    <Form.Group className="mb-3">
                                                                        <Form.Label>Send follow-up after:</Form.Label>
                                                                        <div className="d-flex align-items-center">
                                                                            <Form.Control
                                                                                type="number"
                                                                                size="sm"
                                                                                style={{ width: '80px' }}
                                                                                min={0}
                                                                                max={60}
                                                                                value={template.days}
                                                                                onChange={(e) => handleUpdateFollowUpTemplate(template.id, 'days', e.target.value ? Number(e.target.value) : '')}
                                                                            />
                                                                            <p className="mb-0 ms-2">days</p>
                                                                        </div>
                                                                    </Form.Group>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="text-end">
                                                        <SecondaryButton onClick={() => handleRemoveFollowUpTemplate(template.id)}>Remove</SecondaryButton>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="text-start mt-3">
                                                <PrimaryButton onClick={handleAddFollowUpTemplate}>Add Follow-Up Template</PrimaryButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                  </div>
                </Card>
            </div>
        </div>
    );
};

export default LeadAutomation;
